/**
 * BufferTargetControl — Phase 86.L: the reserve target is the user's call.
 *
 * Three months was only ever a default. This edits `buffer_target_months` and
 * re-reads the whole plan from the response, so the buffer bar, the headline and
 * the shortfall all move together instead of being recalculated twice in two
 * places.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Check, Loader2, Pencil, X } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';

const API = process.env.REACT_APP_BACKEND_URL;
const PRESETS = [1, 3, 6, 12];
const MIN = 0.5;
const MAX = 24;

export default function BufferTargetControl({ months, onSaved, tone = 'hero' }) {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState('');
  const [saving, setSaving] = useState(null);

  const save = async (value) => {
    const n = Number(value);
    if (!Number.isFinite(n) || n < MIN || n > MAX) {
      toast.error(`Pick a target between ${MIN} and ${MAX} months.`);
      return;
    }
    setSaving(n);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await axios.put(`${API}/api/cash-plan/settings`,
        { buffer_target_months: n },
        {
          headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {},
          timeout: 45000,
        });
      onSaved?.(res.data);
      toast.success(`Safety reserve target set to ${n} ${n === 1 ? 'month' : 'months'}.`);
      setOpen(false);
      setCustom('');
    } catch (e) {
      toast.error('Could not save your target. Try again.');
    } finally {
      setSaving(null);
    }
  };

  const light = tone === 'hero';
  const chip = light
    ? 'border-white/25 text-white/85 hover:bg-white/10'
    : 'border-border text-muted-foreground hover:text-evergreen hover:border-evergreen/40';

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] transition-colors ${chip}`}
        data-testid="buffer-target-edit"
      >
        <Pencil className="h-2.5 w-2.5" aria-hidden="true" />
        {months} {months === 1 ? 'month' : 'months'} target
      </button>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap" data-testid="buffer-target-editor">
      <span className={`text-[11px] ${light ? 'text-white/70' : 'text-muted-foreground'}`}>
        Target
      </span>
      {PRESETS.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => save(p)}
          disabled={saving != null}
          className={`rounded-full border px-2 py-0.5 text-[11px] transition-colors ${chip} ${
            Number(months) === p ? (light ? 'bg-white/15' : 'bg-muted') : ''}`}
          data-testid={`buffer-target-preset-${p}`}
        >
          {saving === p ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : `${p}m`}
        </button>
      ))}
      <input
        type="number"
        min={MIN}
        max={MAX}
        step="0.5"
        value={custom}
        onChange={(e) => setCustom(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); save(custom); } }}
        placeholder="custom"
        aria-label="Custom target in months"
        className={`w-16 rounded-full border px-2 py-0.5 text-[11px] bg-transparent ${chip}`}
        data-testid="buffer-target-custom"
      />
      <button type="button" onClick={() => save(custom)} disabled={saving != null || !custom}
              className={`rounded-full border px-1.5 py-0.5 ${chip}`}
              aria-label="Save custom target" data-testid="buffer-target-save">
        <Check className="h-3 w-3" />
      </button>
      <button type="button" onClick={() => { setOpen(false); setCustom(''); }}
              className={`rounded-full border px-1.5 py-0.5 ${chip}`}
              aria-label="Cancel" data-testid="buffer-target-cancel">
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}
